import { useState, useEffect, useCallback, forwardRef, useImperativeHandle, memo, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Dropdown,
  Option,
  Text,
  Spinner,
  Badge,
  Button,
} from '@fluentui/react-components';
import {
  Add20Regular,
} from '@fluentui/react-icons';
import { UserThread } from '../models/types';

// Interface for methods exposed via ref
export interface ThreadSelectorRef {
  clearSelection: () => void;
}

const useStyles = makeStyles({
  dropdown: {
    width: '100%',
    '& .fui-Dropdown__button': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  optionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    width: '100%',
  },
  threadHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  domainBadge: {
    textTransform: 'capitalize',
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  threadQuestion: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  threadTimestamp: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: tokens.spacingVerticalS,
  },
  loadingSpinner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: tokens.spacingVerticalXS,
    color: tokens.colorBrandForeground1,
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
  },
  newConversationButton: {
    minWidth: 'auto',
  },
});

interface ThreadSelectorProps {
  threads: UserThread[];
  threadsLoading: boolean;
  selectedThreadId?: string;
  onThreadSelect: (threadId: string | null) => void;
  onLoadConversation: (threadId: string, authorizationId?: string) => void;
  onRefreshThreads: () => Promise<UserThread[]>;
  onClear?: () => void; // Callback to notify parent when selection is cleared
  disabled?: boolean;
}

export const ThreadSelector = memo(forwardRef<ThreadSelectorRef, ThreadSelectorProps>(({ 
  threads,
  threadsLoading,
  selectedThreadId,
  onThreadSelect,
  onLoadConversation,
  onRefreshThreads,
  onClear,
  disabled = false,
}, ref) => {
  const classes = useStyles();
  const [selectedValue, setSelectedValue] = useState<string>('new-conversation');
  
  // Remove internal thread state and loading - now controlled by parent

  // Update selected value when selectedThreadId prop changes
  useEffect(() => {
    if (selectedThreadId) {
      setSelectedValue(selectedThreadId);
    } else {
      setSelectedValue('new-conversation');
    }
  }, [selectedThreadId]);

  const handleSelectionChange = useCallback((value: string) => {
    console.log('[ThreadSelector] Selection changed to:', value);
    setSelectedValue(value);
    
    if (value === 'new-conversation') {
      onThreadSelect(null);
      onClear?.(); // Notify parent to clear authorization and documents for new conversation
    } else {
      console.log('[ThreadSelector] Loading conversation for thread ID:', value);
      const selectedThread = threads.find(t => t.ThreadId === value);
      const authorizationId = selectedThread?.AuthorizationId;
      console.log('[ThreadSelector] Found authorization ID:', authorizationId);
      
      // Only call onLoadConversation - it handles both thread selection and loading
      // Don't call onThreadSelect separately as it causes duplicate events
      onLoadConversation(value, authorizationId);
    }
  }, [onThreadSelect, onLoadConversation, onClear]);

  const handleClearSelection = useCallback(() => {
    console.log('[ThreadSelector] Clearing selection');
    setSelectedValue('new-conversation');
    onThreadSelect(null);
    onClear?.(); // Notify parent if callback provided
  }, []); // Remove dependencies to prevent re-renders

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    clearSelection: handleClearSelection,
  }), [handleClearSelection]);

  const formatThreadDisplayText = (thread: UserThread): string => {
    const authPart = thread.AuthorizationId || 'General';
    const questionPart = thread.InitialQuestion || 'Conversation';
    const maxLength = 35; // Reduced from 50 to fit better in dropdown
    
    const displayText = `${authPart} - ${questionPart}`;
    
    if (displayText.length > maxLength) {
      return displayText.substring(0, maxLength - 3) + '...';
    }
    
    return displayText;
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
      
      if (diffInHours < 1) {
        return 'Just now';
      } else if (diffInHours < 24) {
        return `${Math.floor(diffInHours)}h ago`;
      } else if (diffInHours < 24 * 7) {
        return `${Math.floor(diffInHours / 24)}d ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch {
      return 'Unknown';
    }
  };

  const getCurrentSelectionText = (): string => {
    if (selectedValue === 'new-conversation') {
      return 'New Conversation';
    }
    
    const selectedThread = threads.find(t => t.ThreadId === selectedValue);
    if (selectedThread) {
      return formatThreadDisplayText(selectedThread);
    }
    
    return 'Select Conversation';
  };

  // Memoize thread options to prevent re-rendering when only selection changes
  const threadOptions = useMemo(() => {
    return threads.map((thread) => (
        <Option key={thread.ThreadId} value={thread.ThreadId} text={formatThreadDisplayText(thread)}>
          <div className={classes.optionContent}>
            <div className={classes.threadHeader}>
              <Text>{thread.AuthorizationId || 'General'}</Text>
              {thread.Domain && (
                <>
                  <Text> - </Text>
                  <Text style={{ textTransform: 'capitalize' }}>{thread.Domain}</Text>
                </>
              )}
              {!thread.IsActive && (
                <Badge size="extra-small" color="warning">
                  Inactive
                </Badge>
              )}
            </div>
            <Text className={classes.threadQuestion}>
              {thread.InitialQuestion || 'No question recorded'}
            </Text>
            <Text className={classes.threadTimestamp}>
              {formatTimestamp(thread.CreatedAt)}
            </Text>
          </div>
        </Option>
    ));
  }, [threads, classes]); // Only re-memoize when threads or classes change

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, width: '100%' }}>
      <Dropdown
        className={classes.dropdown}
        value={getCurrentSelectionText()}
        selectedOptions={[selectedValue]}
        onOptionSelect={(_, data) => {
          if (data.optionValue) {
            handleSelectionChange(data.optionValue);
          }
        }}
        disabled={disabled}
        placeholder="Select conversation..."
      >
        {/* New Conversation Option */}
        <Option key="new-conversation" value="new-conversation" text="New Conversation">
          <div className={classes.optionContent}>
            <div className={classes.threadHeader}>
              <Add20Regular />
              <Text>New Conversation</Text>
            </div>
            <Text className={classes.threadQuestion}>
              Start a fresh conversation
            </Text>
          </div>
        </Option>

        {/* Loading State */}
        {threadsLoading && (
          <Option key="loading" value="loading" text="Loading conversations..." disabled>
            <div className={classes.loadingContainer}>
              <Spinner size="tiny" />
              <Text>Loading conversations...</Text>
            </div>
          </Option>
        )}

        {/* Empty State - only show when not loading and no threads */}
        {!threadsLoading && threads.length === 0 && (
          <Option key="empty" value="empty" text="No conversation history found" disabled>
            <div className={classes.emptyState}>
              <Text>No conversation history found</Text>
            </div>
          </Option>
        )}

        {/* Thread Options */}
        {!threadsLoading && threadOptions}
        </Dropdown>

      {/* Refresh Button */}
      {threadsLoading ? (
        <div className={classes.loadingSpinner}>
          <Spinner size="tiny" />
        </div>
      ) : (
        <Button
          size="small"
          appearance="subtle"
          onClick={onRefreshThreads}
          disabled={disabled}
          title="Refresh conversation history"
        >
          Refresh
        </Button>
      )}
    </div>
  );
}));

ThreadSelector.displayName = 'ThreadSelector';

export default ThreadSelector;